import { useQueryClient } from '@tanstack/react-query';
import { Eye, LogOut, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { setRolePreview } from '@/api/client';
import { useStopActAs } from '@/api/queries/platform';
import { useSession } from '@/auth/use-session';
import { Button } from '@/components/ui/button';

const minutesLeft = (iso: string) =>
  Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 60_000));

/**
 * Step 15: a banner while you see the app as someone else: Tekansa support acting as a user,
 * or an admin previewing a role (both read-only). Step 17e: a Super Admin's "Log in as" (full
 * access) says whose account this is and leads back to Super Admin.
 */
export function ViewingBanner() {
  const session = useSession();
  const qc = useQueryClient();
  const stop = useStopActAs();
  const [, tick] = useState(0);
  useEffect(() => {
    if (!session.actAs) return;
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [session.actAs]);

  if (session.actAs?.mode === 'full')
    return (
      <div
        role="status"
        className="flex flex-wrap items-center gap-3 border-b border-primary/30 bg-primary/10 px-4 py-2 text-sm text-foreground"
      >
        <ShieldCheck aria-hidden="true" className="size-4 shrink-0 text-primary" />
        <span className="min-w-0 flex-1">
          You are inside <strong>{session.tenant?.name}</strong> as{' '}
          <strong>{session.user.name}</strong>. Every change is recorded in its audit log.
        </span>
        <Button size="sm" onClick={() => stop.mutate()} disabled={stop.isPending}>
          <LogOut aria-hidden="true" />
          Return to Super Admin
        </Button>
      </div>
    );
  if (session.actAs)
    return (
      <div
        role="status"
        className="flex flex-wrap items-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-100"
      >
        <Eye aria-hidden="true" className="size-4 shrink-0" />
        <span className="min-w-0 flex-1">
          Viewing as <strong>{session.user.name}</strong> ({session.tenant?.name}), read-only,{' '}
          {minutesLeft(session.actAs.expiresAt)} min left. Reason: {session.actAs.reason}
        </span>
        <Button size="sm" variant="outline" onClick={() => stop.mutate()} disabled={stop.isPending}>
          <LogOut aria-hidden="true" />
          Stop viewing
        </Button>
      </div>
    );
  if (session.preview)
    return (
      <div
        role="status"
        className="flex flex-wrap items-center gap-3 border-b border-brand-200 bg-brand-50 px-4 py-2 text-sm text-brand-950 dark:border-brand-800 dark:bg-brand-950/60 dark:text-brand-100"
      >
        <Eye aria-hidden="true" className="size-4 shrink-0" />
        <span className="min-w-0 flex-1">
          Previewing the role <strong>{session.preview.roleName}</strong>: you see what it sees.
          Nothing can be changed.
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setRolePreview(null);
            void qc.invalidateQueries();
          }}
        >
          Exit preview
        </Button>
      </div>
    );
  return null;
}
